import { useState } from 'react';
import type { FormEvent } from 'react';
import { QUESTION_MAX, formatDate } from '@iac/contracts';
import type { BrainNoteDto } from '@iac/contracts';
import { EmptyState } from '../../components/EmptyState';
import { IconPencilSquare } from '../../components/icons';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useAsk } from './api';
import { useLocale } from '../../lib/locale';
import { brainDict } from './i18n';
import { NoteDialog } from './NoteDialog';

/** Enough to get back to what was just asked without turning into a history. */
const RECENT_MAX = 5;

/**
 * One question, one result. The API answers each question on its own and the
 * model never sees an earlier one, so the screen does not pretend otherwise:
 * a new question replaces the result instead of extending a transcript.
 */
export function BrainAsk() {
  const t = useDict(brainDict);
  usePageTitle(t.askTitle);

  const [question, setQuestion] = useState('');
  // Page memory only: questions about addresses and PINs should not outlive
  // the visit in the browser's storage.
  const [recent, setRecent] = useState<string[]>([]);
  const [editing, setEditing] = useState<BrainNoteDto | null>(null);

  // The shown result is the mutation itself — `variables` is the question
  // while it is pending and after it settles, so there is no copy to keep in
  // step with it.
  const ask = useAsk();
  const shown = ask.isPending || ask.isSuccess ? ask.variables : undefined;

  /** The form, a chip and a corrected note all ask through here. */
  function submit(value: string) {
    if (ask.isPending) return;
    ask.mutate(value, {
      // Only answered questions become chips: one that failed is back in the
      // field already, and a chip for it would only fail again.
      onSuccess: () =>
        setRecent((prev) =>
          [value, ...prev.filter((q) => q !== value)].slice(0, RECENT_MAX),
        ),
      // The question goes back where it was typed, unless something has been
      // typed since, which is the one thing worth more than the restore.
      onError: () =>
        setQuestion((current) => (current === '' ? value : current)),
    });
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const value = question.trim();
    if (value === '' || ask.isPending) return;
    setQuestion('');
    submit(value);
  }

  return (
    <div className="ask-page">
      <form className="ask-form" onSubmit={onSubmit}>
        <label htmlFor="ask-input" className="ask-input-label">
          {t.questionLabel}
        </label>
        {/* A question is one line, so this is an input, not a textarea:
            Enter submits natively and the height matches the button.

            Password managers attach by sniffing the placeholder and label,
            so a question mentioning a phone or an address gets an autofill
            popup over it. `autoComplete="off"` alone does not stop them —
            the per-vendor opt-outs below do, and a generic placeholder
            keeps the heuristics from firing at all. */}
        <input
          id="ask-input"
          type="text"
          className="field-input ask-input"
          required
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          maxLength={QUESTION_MAX}
          placeholder={t.questionPlaceholder}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={ask.isPending}>
          {t.ask}
        </button>
      </form>

      {recent.length > 0 && (
        <div role="group" aria-label={t.recentQuestions} className="ask-recent">
          {recent.map((q) => (
            <button
              key={q}
              type="button"
              className="ask-recent-chip"
              disabled={ask.isPending}
              onClick={() => submit(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {shown === undefined ? (
        <EmptyState>{t.askEmpty}</EmptyState>
      ) : (
        <section className="ask-result" aria-live="polite">
          <h2 className="ask-question">{shown}</h2>
          {ask.isSuccess ? (
            <>
              <p className="ask-answer">{ask.data.answer ?? t.noAnswer}</p>
              {ask.data.sources.length > 0 && (
                <Sources notes={ask.data.sources} onEdit={setEditing} />
              )}
            </>
          ) : (
            <p className="ask-placeholder">
              <span className="ask-placeholder-label">{t.thinking}</span>
            </p>
          )}
        </section>
      )}

      {/* A corrected note is embedded anew, so the answer on screen may no
          longer hold — ask again rather than leave it standing. */}
      <NoteDialog
        open={editing !== null}
        note={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSaved={() => {
          if (shown !== undefined) submit(shown);
        }}
      />
    </div>
  );
}

/**
 * The notes the answer cites, in the open: they are how the reader checks it,
 * and the way to fix it when a note is out of date. Deleting stays on the
 * Notes page — one tap from an answer is too close to losing a fact.
 */
function Sources({
  notes,
  onEdit,
}: {
  notes: BrainNoteDto[];
  onEdit: (note: BrainNoteDto) => void;
}) {
  const { locale } = useLocale();
  const t = useDict(brainDict);
  return (
    <div className="ask-sources">
      <h3 className="ask-sources-heading">{t.fromNotes(notes.length)}</h3>
      <ul className="ask-source-list">
        {notes.map((note) => (
          <li key={note.id} className="ask-source">
            <div className="ask-source-body">
              <span className="text-hint">
                {formatDate(note.createdAt, locale)}
              </span>
              <p className="ask-source-text">{note.content}</p>
            </div>
            <button
              type="button"
              className="btn-icon-ghost"
              aria-label={t.editNote}
              title={t.editNote}
              onClick={() => onEdit(note)}
            >
              <IconPencilSquare />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
